import * as Model from '@mms-v3/data-model-js';
import { Field } from '@mms-v3/data-model-js';
import { snakeToCamel } from './utils';
import { Validation, ValidationResult, ValidationResults } from './interfaces';
import {
  validateEmail,
  validateExternalId,
  validateIssuedLength,
  validateMinGeoLocationPoints,
  validateOneMainName,
  validateRequired,
} from './validators';
import { checkIsOwnRecord } from '../../Auth/utils/DataModel';
import { SessionState } from '../../Auth/utils/interfaces';
import { PubTypeGroups } from '../../Settings/validationWorkEntityMapping';
import { PageValidations } from '../../Settings/PageValidations';
import { EntityAdminDTO, EntityDTO, EntityType } from '../types';
import { ExternalIdDTO, PersonNameDTO, WorkGeoLocationPointDTO } from '../../api/core';

interface Validator {
  check: boolean;
  fn: (value: any) => ValidationResult;
}

export const validate = (rootData: EntityDTO, adminData: EntityAdminDTO, entity: EntityType, session: SessionState, pubtype?: PubTypeGroups): Validation => {
  const entityName: string = `${entity}${pubtype ? `_${pubtype}` : ''}`;
  // Load entity model
  const Entity: Model.Entity = Model.DataModel.for(entityName);


  if (!Entity) {
    throw new Error(`Entity ${entityName} not found`);
  }

  // Get fields of entity, work has generic and pubtypeGroup related fields to be loaded and joined together
  const rootFields: Model.Field[] = [...entity === 'work' ? Model.DataModel.for('work_generic').fields : [], ...Entity.fields];
  const adminFields: Model.Field[] = Model.DataModel.for(`${entity}_admin_data`).fields;

  // Get user status (roles, ownership)
  const isOwnRecord: boolean = session ? checkIsOwnRecord(session, rootData.id === undefined, [session.user_displayname]) : false;
  const roles: string[] = session?.roles || [];

  const root: ValidationResults = validateFields(rootFields, rootData, '', {roles, isOwnRecord});
  const admin: ValidationResults = validateFields(adminFields, adminData, '', {roles, isOwnRecord});
  const valid: boolean = root.valid && admin.valid;

  const pages = {};

  if (entity === 'work') {
    Object.entries(PageValidations).forEach(([page, fields]: [string, string[]]): any => {
      pages[page] = fields.every((field: string): boolean => {
        if (page === 'administrative') {
          return admin?.fields[field] ? admin?.fields[field]?.valid : true;
        } else {
          return root?.fields[field] ? root?.fields[field]?.valid : true;
        }
      });
    });
  }

  return {
    valid,
    root,
    admin,
    pages,
  };
};

export const validateFields = (fields: Field[], dataObject: EntityDTO | EntityAdminDTO | unknown, path: string = '', config: {
  roles: string[];
  isOwnRecord: boolean;
}): ValidationResults => {
  const results: ValidationResults = {valid: true, fields: {}};
  fields.forEach((field: Field): void => {
    // get data
    const data: unknown | undefined = dataObject ? dataObject[snakeToCamel(field.name)] : undefined;
    // VALIDATION

    const validators: Validator[] = [
      {
        check: field.mustWrite(config.roles, config.isOwnRecord),
        fn: (value: any): ValidationResult => validateRequired(value),
      },
      { check: field.name === 'issued', fn: (value: number): ValidationResult => validateIssuedLength(value) },
      {
        check: field.name === 'external_id',
        fn: (value: string): ValidationResult => validateExternalId(value, (dataObject as ExternalIdDTO).type),
      },
      {
        check: field.name === 'person_names',
        fn: (value: PersonNameDTO[]): ValidationResult => validateOneMainName(value),
      },
      {
        check: field.name === 'work_geo_location_points',
        fn: (value: WorkGeoLocationPointDTO[]): ValidationResult => validateMinGeoLocationPoints(value),
      },
      {
        check: field.name === 'email',
        fn: (value: string): ValidationResult => validateEmail(value),
      },
    ];
    const failedResults: ValidationResult[] = validators
      .filter((validator: Validator): boolean => validator.check)
      .map((validator: Validator): ValidationResult => validator.fn(data))
      .filter((validationResult: ValidationResult): boolean => !validationResult.valid);

    const result: ValidationResult = {
      valid: failedResults.length === 0,
      reason: failedResults.map((validationResult: ValidationResult): string => validationResult.reason).join(';'),
    };

    // validate children
    if (field.fields) {
      if (Array.isArray(data)) {
        result.children = (data || []).map((childData: any, index: number): ValidationResults => validateFields(field.fields, childData, `${path}.${field.name}.${index}`, config));
        if (result.valid && result.children.length > 0) {
          result.valid = result.children.every((child: ValidationResults): boolean => child.valid);
        }
      } else if (data instanceof Set) {
        // TODO workaround for sets
        result.children = (Array.from(data || [])).map(
          (childData: any, index: number): ValidationResults =>
            validateFields(field.fields, childData, `${path}.${field.name}.${index}`, config)
        );
        if (result.valid && result.children.length > 0) {
          result.valid = result.children.every((child: ValidationResults): boolean => child.valid);
        }
      } else {
        if (data) {
          result.child = validateFields(field.fields, data, `${path}.${field.name}`, config);
          if (result.valid) {
            result.valid = result.child.valid;
          }
        }
      }
    }
    // persist result
    if (!result.valid) {
      results.valid = false;
    }

    results.fields[field.name] = result;
  });

  return results;
};
